import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Row,
  Col,
  Select,
  message,
  Modal,
  Form,
  Popconfirm,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { wxAppService } from '../../services/wxAppService';
import {
  WxAppVO,
  WxAppQueryRequest,
  WxAppSaveRequest,
  MerchantOption,
  Page,
} from '../../types';

const { Option } = Select;
const { Text } = Typography;

const WxAppList: React.FC = () => {
  const [list, setList] = useState<WxAppVO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState<Partial<WxAppQueryRequest>>({});

  // 新增/编辑弹窗
  const [formVisible, setFormVisible] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState<WxAppVO | null>(null);
  const [form] = Form.useForm<WxAppSaveRequest>();

  // 绑定商户号弹窗
  const [bindVisible, setBindVisible] = useState(false);
  const [bindLoading, setBindLoading] = useState(false);
  const [bindingApp, setBindingApp] = useState<WxAppVO | null>(null);
  const [merchantOptions, setMerchantOptions] = useState<MerchantOption[]>([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState<number | undefined>(undefined);

  const loadList = useCallback(async (page = 1, pageSize = 10, params?: Partial<WxAppQueryRequest>) => {
    setLoading(true);
    try {
      const query: WxAppQueryRequest = {
        pageNo: page,
        pageSize,
        ...(params ?? filters),
      };
      const res = await wxAppService.getList(query);
      if (res.status === 1) {
        const data = res.data as Page<WxAppVO>;
        setList(data.content || []);
        setPagination({ current: page, pageSize, total: data.totalElements || 0 });
      } else {
        message.error(res.msg || '获取小程序列表失败');
      }
    } catch {
      message.error('获取小程序列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadList(1, 10, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadList(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const empty: Partial<WxAppQueryRequest> = {};
    setFilters(empty);
    loadList(1, pagination.pageSize, empty);
  };

  const handleTableChange = (pag: any) => {
    loadList(pag.current, pag.pageSize);
  };

  // 打开新增弹窗
  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 1, appType: 1 });
    setFormVisible(true);
  };

  // 打开编辑弹窗
  const openEditModal = (record: WxAppVO) => {
    setEditingRecord(record);
    form.setFieldsValue({
      appId: record.appId,
      appSecret: record.appSecret,
      appType: record.appType,
      appName: record.appName,
      appOriginalId: record.appOriginalId,
      appQrcode: record.appQrcode,
      status: record.status,
      remark: record.remark,
    });
    setFormVisible(true);
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setFormLoading(true);
      const payload: WxAppSaveRequest = { ...values };
      let res;
      if (editingRecord) {
        res = await wxAppService.update({ ...payload, id: editingRecord.id });
      } else {
        res = await wxAppService.save(payload);
      }
      if (res.status === 1) {
        message.success(editingRecord ? '修改成功' : '新增成功');
        setFormVisible(false);
        loadList(pagination.current, pagination.pageSize);
      }
    } catch {
      // form validation error, ignore
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await wxAppService.delete(id);
      if (res.status === 1) {
        message.success('删除成功');
        loadList(pagination.current, pagination.pageSize);
      }
    } catch {
      message.error('删除失败');
    }
  };

  // 打开绑定商户号弹窗
  const openBindModal = async (record: WxAppVO) => {
    setBindingApp(record);
    setSelectedMerchantId(record.boundMerchantId ?? undefined);
    setBindVisible(true);
    try {
      const res = await wxAppService.getMerchantOptions();
      if (res.status === 1) {
        setMerchantOptions(res.data || []);
      }
    } catch {
      message.error('获取商户号列表失败');
    }
  };

  const handleBind = async () => {
    if (!bindingApp || selectedMerchantId === undefined) {
      message.warning('请选择商户号');
      return;
    }
    setBindLoading(true);
    try {
      const res = await wxAppService.bindMerchant(bindingApp.id, selectedMerchantId);
      if (res.status === 1) {
        message.success('绑定成功');
        setBindVisible(false);
        loadList(pagination.current, pagination.pageSize);
      }
    } catch {
      message.error('绑定失败');
    } finally {
      setBindLoading(false);
    }
  };

  const handleUnbind = async (record: WxAppVO) => {
    try {
      const res = await wxAppService.unbindMerchant(record.id);
      if (res.status === 1) {
        message.success('解绑成功');
        loadList(pagination.current, pagination.pageSize);
      }
    } catch {
      message.error('解绑失败');
    }
  };

  const columns: ColumnsType<WxAppVO> = [
    {
      title: '应用名称',
      dataIndex: 'appName',
      key: 'appName',
      width: 150,
    },
    {
      title: 'AppID',
      dataIndex: 'appId',
      key: 'appId',
      width: 200,
      render: (text: string) => <Text copyable code>{text}</Text>,
    },
    {
      title: '类型',
      dataIndex: 'appTypeText',
      key: 'appTypeText',
      width: 90,
      render: (text: string, record: WxAppVO) => (
        <Tag color={record.appType === 1 ? 'blue' : 'purple'}>{text}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number, record: WxAppVO) => (
        <Tag color={status === 1 ? 'success' : 'default'}>{record.statusText}</Tag>
      ),
    },
    {
      title: '绑定商户号',
      key: 'boundMerchant',
      width: 180,
      render: (_: any, record: WxAppVO) => {
        if (record.boundMchId) {
          return (
            <Space size={4}>
              <Text type="success">{record.boundMerchantName}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>({record.boundMchId})</Text>
            </Space>
          );
        }
        return <Text type="secondary">未绑定</Text>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (text: string) => text ? text.replace('T', ' ') : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 200,
      render: (_: any, record: WxAppVO) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          {record.boundMchId ? (
            <Popconfirm
              title="确认解绑该商户号吗？"
              onConfirm={() => handleUnbind(record)}
              okText="确认"
              cancelText="取消"
            >
              <Button type="link" size="small" icon={<DisconnectOutlined />} danger>
                解绑
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => openBindModal(record)}
            >
              绑定
            </Button>
          )}
          {record.boundMchId && (
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => openBindModal(record)}
            >
              换绑
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          小程序列表
          <Tag color="orange">老通道</Tag>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          新增小程序
        </Button>
      }
    >
      {/* 筛选区域 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="应用名称"
            allowClear
            style={{ width: 180 }}
            value={filters.appName}
            onChange={e => setFilters(f => ({ ...f, appName: e.target.value || undefined }))}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Select
            placeholder="应用类型"
            allowClear
            style={{ width: 130 }}
            value={filters.appType}
            onChange={v => setFilters(f => ({ ...f, appType: v }))}
          >
            <Option value={1}>小程序</Option>
            <Option value={2}>公众号</Option>
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 110 }}
            value={filters.status}
            onChange={v => setFilters(f => ({ ...f, status: v }))}
          >
            <Option value={1}>启用</Option>
            <Option value={0}>禁用</Option>
          </Select>
        </Col>
        <Col>
          <Space>
            <Button type="primary" onClick={handleSearch}>查询</Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Col>
      </Row>

      {/* 数据表格 */}
      <Table<WxAppVO>
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: total => `共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑小程序' : '新增小程序'}
        open={formVisible}
        onOk={handleFormSubmit}
        onCancel={() => setFormVisible(false)}
        confirmLoading={formLoading}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="微信 AppID"
                name="appId"
                rules={[
                  { required: true, message: '请输入微信AppID' },
                  { max: 32, message: '最多32个字符' },
                ]}
              >
                <Input placeholder="wx..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="AppSecret"
                name="appSecret"
                rules={[
                  { required: true, message: '请输入AppSecret' },
                  { max: 64, message: '最多64个字符' },
                ]}
              >
                <Input.Password placeholder="请输入AppSecret" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="应用名称"
                name="appName"
                rules={[
                  { required: true, message: '请输入应用名称' },
                  { max: 64, message: '最多64个字符' },
                ]}
              >
                <Input placeholder="请输入应用名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="应用类型"
                name="appType"
                rules={[{ required: true, message: '请选择应用类型' }]}
              >
                <Select placeholder="请选择类型">
                  <Option value={1}>小程序</Option>
                  <Option value={2}>公众号</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="原始ID"
                name="appOriginalId"
                rules={[{ max: 64, message: '最多64个字符' }]}
              >
                <Input placeholder="gh_xxxxx（可选）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Option value={1}>启用</Option>
                  <Option value={0}>禁用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="二维码 URL"
            name="appQrcode"
            rules={[{ max: 256, message: '最多256个字符' }]}
          >
            <Input placeholder="https://...（可选）" />
          </Form.Item>
          <Form.Item
            label="备注"
            name="remark"
            rules={[{ max: 255, message: '最多255个字符' }]}
          >
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 绑定商户号弹窗 */}
      <Modal
        title={`绑定商户号 — ${bindingApp?.appName ?? ''}`}
        open={bindVisible}
        onOk={handleBind}
        onCancel={() => setBindVisible(false)}
        confirmLoading={bindLoading}
        okText="确认绑定"
        destroyOnClose
      >
        <div style={{ margin: '16px 0' }}>
          {bindingApp?.boundMchId && (
            <div style={{ marginBottom: 12 }}>
              当前已绑定：<Text strong>{bindingApp.boundMerchantName}</Text>
              <Text type="secondary">（{bindingApp.boundMchId}）</Text>，选择新商户号后将自动替换。
            </div>
          )}
          <Select
            style={{ width: '100%' }}
            placeholder="请选择要绑定的商户号"
            value={selectedMerchantId}
            onChange={v => setSelectedMerchantId(v)}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={merchantOptions.map(m => ({
              value: m.id,
              label: `${m.mchName}（${m.mchId}）`,
            }))}
          />
        </div>
      </Modal>
    </Card>
  );
};

export default WxAppList;
